import React, { useState, useEffect } from 'react';
import { useAppSelector, useAppDispatch } from '../../../store';
import { selectDbData } from '../../../store/selectors/databaseSelectors';
import { addMspBuss, addMspBreaker, addMspManufacturer, addMspLocation, addMspStyle, addMspType } from '../../../store/slices/databaseSlice';
import { mspService } from '../../../services';
import { Msp, MspCategory } from '../../../types';
import { TOAST_CONFIG, TOAST_WARNING_CONFIG } from '../../../constants';
import toast from 'react-hot-toast';

interface MspForm {
  category: string;
  value: string;
}

type MspTab = 'buss' | 'breaker' | 'manufacturer' | 'location' | 'style' | 'type';

const MSP_TABS: { id: MspTab; label: string; category: MspCategory }[] = [
  { id: 'buss', label: 'Buss Rating', category: MspCategory.MSP_BUSS },
  { id: 'breaker', label: 'Breaker Rating', category: MspCategory.MSP_BREAKER },
  { id: 'manufacturer', label: 'Manufacturer', category: MspCategory.MSP_MANUFACTURER },
  { id: 'location', label: 'Location', category: MspCategory.MSP_LOCATION },
  { id: 'style', label: 'Style', category: MspCategory.MSP_STYLE },
  { id: 'type', label: 'Type', category: MspCategory.MSP_TYPE }
];

export const MspManagement: React.FC = () => {
  const dispatch = useAppDispatch();
  const dbData = useAppSelector(selectDbData);
  
  // Form states
  const [mspForm, setMspForm] = useState<MspForm>({
    category: MspCategory.MSP_BUSS,
    value: ''
  });
  
  // UI states
  const [activeTab, setActiveTab] = useState<MspTab>('buss');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [localMspData, setLocalMspData] = useState(dbData.msp);

  // Initialize local state with Redux data
  useEffect(() => {
    setLocalMspData(dbData.msp);
  }, [dbData.msp]);

  // Update form category when tab changes
  useEffect(() => {
    const currentTab = MSP_TABS.find(tab => tab.id === activeTab);
    if (currentTab) {
      setMspForm(prev => ({ ...prev, category: currentTab.category }));
    }
  }, [activeTab]);

  // Get current data for active tab
  const getCurrentData = (): Msp[] => {
    switch (activeTab) {
      case 'buss': return localMspData.busses;
      case 'breaker': return localMspData.breakers;
      case 'manufacturer': return localMspData.manufacturers;
      case 'location': return localMspData.locations;
      case 'style': return localMspData.styles;
      case 'type': return localMspData.types;
      default: return [];
    }
  };

  // Get dispatch function for active tab
  const getDispatchFunction = () => {
    switch (activeTab) {
      case 'buss': return addMspBuss;
      case 'breaker': return addMspBreaker;
      case 'manufacturer': return addMspManufacturer;
      case 'location': return addMspLocation;
      case 'style': return addMspStyle;
      case 'type': return addMspType;
      default: return addMspBuss;
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!mspForm.value.trim()) {
      toast('Please enter a value', TOAST_WARNING_CONFIG);
      return;
    }

    setIsSubmitting(true);
    try {
      const currentData = getCurrentData();
      const displayOrder = currentData.length + 1;
      
      // Store pure value without any suffix
      // Uppercase for manufacturer, location, style, and type
      let finalValue = mspForm.value.trim();
      if (['manufacturer', 'location', 'style', 'type'].includes(activeTab)) {
        finalValue = finalValue.toUpperCase();
      }
      
      const newMsp = await mspService.createMsp({
        category: mspForm.category,
        value: finalValue,
        displayOrder: displayOrder
      });
      
      // Update Redux state
      const dispatchFunction = getDispatchFunction();
      dispatch(dispatchFunction(newMsp));
      
      // Update local state
      setLocalMspData(prev => {
        const updated = { ...prev };
        switch (activeTab) {
          case 'buss': updated.busses = [...prev.busses, newMsp]; break;
          case 'breaker': updated.breakers = [...prev.breakers, newMsp]; break;
          case 'manufacturer': updated.manufacturers = [...prev.manufacturers, newMsp]; break;
          case 'location': updated.locations = [...prev.locations, newMsp]; break;
          case 'style': updated.styles = [...prev.styles, newMsp]; break;
          case 'type': updated.types = [...prev.types, newMsp]; break;
        }
        return updated;
      });
      
      // Reset form
      setMspForm(prev => ({ ...prev, value: '' }));
      
      toast.success(`MSP ${MSP_TABS.find(t => t.id === activeTab)?.label} created successfully!`, TOAST_CONFIG.toastOptions.success);
    } catch (error) {
      console.error('Error creating MSP item:', error);
      toast.error('Failed to create MSP item. Please try again.', TOAST_CONFIG.toastOptions.error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const currentData = getCurrentData();

  return (
    <div className="p-4">
      {/* Tabs */}
      <div className="flex flex-wrap border-b border-slate-200 mb-4">
        {MSP_TABS.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-3 py-2 font-medium text-sm border-b-2 transition-colors duration-200 mr-4 mb-2 ${
              activeTab === tab.id
                ? 'border-blue-500 text-blue-600'
                : 'border-transparent text-slate-500 hover:text-slate-700'
            }`}
          >
            {tab.label} ({currentData.length})
          </button>
        ))}
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        {/* Form Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <h3 className="text-base font-semibold text-slate-900 mb-3">
            Add New {MSP_TABS.find(t => t.id === activeTab)?.label}
          </h3>

          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-xs font-medium text-slate-700 mb-1">
                {(activeTab === 'buss' || activeTab === 'breaker') ? 'Amperage *' : 'Value *'}
              </label>
              {(activeTab === 'buss' || activeTab === 'breaker') ? (
                <div className="relative">
                  <input
                    type="number"
                    min="1"
                    value={mspForm.value}
                    onChange={(e) => setMspForm(prev => ({ ...prev, value: e.target.value }))}
                    placeholder={activeTab === 'buss' ? 'e.g., 200' : 'e.g., 30'}
                    className="w-full px-3 py-2 pr-8 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    required
                  />
                  <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-sm text-slate-500">A</span>
                </div>
              ) : (
                <input
                  type="text"
                  value={mspForm.value}
                  onChange={(e) => setMspForm(prev => ({ ...prev, value: e.target.value }))}
                  placeholder={`e.g., ${activeTab === 'manufacturer' ? 'Square D' : activeTab === 'location' ? 'Garage' : activeTab === 'style' ? 'Surface Mount' : 'Main Breaker'}`}
                  className="w-full px-2 py-1.5 text-xs border border-slate-200 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                  required
                />
              )}
              {(activeTab === 'buss' || activeTab === 'breaker') && (
                <p className="text-xs text-slate-500 mt-1">Enter numeric value only. "A" will be displayed in the list.</p>
              )}
              {['manufacturer', 'location', 'style', 'type'].includes(activeTab) && (
                <p className="text-xs text-slate-500 mt-1">Text will be automatically converted to uppercase.</p>
              )}
            </div>
            
            <button
              type="submit"
              disabled={isSubmitting || !mspForm.value.trim()}
              className="w-full py-2 px-4 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-md disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
            >
              {isSubmitting ? 'Creating...' : `Create ${MSP_TABS.find(t => t.id === activeTab)?.label}`}
            </button>
          </form>
        </div>

        {/* List Section */}
        <div className="bg-white rounded-lg p-4 border border-slate-200 shadow-sm">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-base font-semibold text-slate-900">
              Existing {MSP_TABS.find(t => t.id === activeTab)?.label}s
            </h3>
            <span className="text-xs text-slate-500">
              {currentData.length} items
            </span>
          </div>

          <div className="max-h-80 overflow-y-auto">
            {currentData.length > 0 ? (
              <div className="flex flex-wrap gap-2 p-2">
                {currentData.map((item) => (
                  <div key={item.id} className="inline-flex items-center px-3 py-1 bg-indigo-50 text-indigo-800 rounded-full border border-indigo-200 hover:bg-indigo-100 transition-colors">
                    <span className="text-xs font-medium">
                      {item.value}{(activeTab === 'buss' || activeTab === 'breaker') ? 'A' : ''}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              <p className="text-slate-500 text-xs text-center py-8">No {MSP_TABS.find(t => t.id === activeTab)?.label?.toLowerCase()}s found</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};